export const LOCAL_GUEST_STORAGE_KEY = "bdc.clubhouse.guest.v1";

export type LocalGuest = {
  schema: "bdc.local-guest.v1";
  id: string;
  handle: string;
  createdAt: string;
  lastSeenAt: string;
};

export type LocalGuestResult = {
  guest: LocalGuest;
  /** False when the browser denied storage: the guest lives in memory only. */
  persisted: boolean;
};

// Some private-browsing/policy modes throw on the mere ACCESS of
// window.localStorage, and others throw only on setItem. Guest entry must
// survive both (AUDIT P1-15/W4 P1-7): the HttpOnly server session is
// authoritative, the local record is just a continuity hint.
function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// Keeps repeat calls on the same page consistent when storage is denied, so
// entry, signup, and retries all talk about the same guest identity.
let memoryGuest: LocalGuest | null = null;

function isLocalGuest(value: unknown): value is LocalGuest {
  if (!value || typeof value !== "object") return false;
  const guest = value as Partial<LocalGuest>;
  return guest.schema === "bdc.local-guest.v1"
    && typeof guest.id === "string"
    && guest.id.startsWith("guest_")
    && typeof guest.handle === "string"
    && typeof guest.createdAt === "string"
    && typeof guest.lastSeenAt === "string";
}

export function readLocalGuest(storage: Storage | null = safeStorage()): LocalGuest | null {
  try {
    const stored = storage?.getItem(LOCAL_GUEST_STORAGE_KEY);
    if (!stored) return memoryGuest;
    const parsed: unknown = JSON.parse(stored);
    return isLocalGuest(parsed) ? parsed : memoryGuest;
  } catch {
    return memoryGuest;
  }
}

function createGuestId() {
  if (typeof crypto.randomUUID === "function") return `guest_${crypto.randomUUID()}`;
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return `guest_${Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("")}`;
}

function writeLocalGuest(storage: Storage | null, guest: LocalGuest): boolean {
  memoryGuest = guest;
  if (!storage) return false;
  try {
    storage.setItem(LOCAL_GUEST_STORAGE_KEY, JSON.stringify(guest));
    return true;
  } catch {
    return false;
  }
}

export function getOrCreateLocalGuest(storage: Storage | null = safeStorage()): LocalGuest {
  return getOrCreateLocalGuestSafe(storage).guest;
}

export function getOrCreateLocalGuestSafe(storage: Storage | null = safeStorage()): LocalGuestResult {
  const existing = readLocalGuest(storage);
  const now = new Date().toISOString();
  if (existing) {
    const returning = { ...existing, lastSeenAt: now };
    return { guest: returning, persisted: writeLocalGuest(storage, returning) };
  }

  const id = createGuestId();
  const guest: LocalGuest = {
    schema: "bdc.local-guest.v1",
    id,
    handle: `Guest #${id.slice(-4).toUpperCase()}`,
    createdAt: now,
    lastSeenAt: now,
  };
  return { guest, persisted: writeLocalGuest(storage, guest) };
}
